/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Button, Spin, Typography } from 'antd';
import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useProjectId } from '~src/hooks/useProjectId';
import { useProjectBuilder } from '~src/hooks/api-hooks/useProjectBuilder';
import { useTemplateArchiveFolderStructure } from '~src/hooks/api-hooks/useProjectBuilderModal';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DraggableFolderList } from '../components/DraggableFolderList';
import { DroppableFolderList } from '../components/DroppableFolderList';
import { DbFolder } from '~src/types/db';

const { Title } = Typography;

interface FolderBuilderStepProps {
  templateId: string | null;
  customFolders: any[];
  setCustomFolders: (folders: any[]) => void;
}

export const FolderBuilderStep: React.FC<FolderBuilderStepProps> = ({
  templateId,
  customFolders,
  setCustomFolders,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();

  const projectFoldersQuery = useProjectBuilder(projectId);
  const templateFoldersQuery = useTemplateArchiveFolderStructure(
    projectId,
    templateId,
    !!templateId
  );

  const [rightSideFolders, setRightSideFolders] = useState<any[]>([]);
  const [nextId, setNextId] = useState(-1);

  useEffect(() => {
    if (customFolders.length > 0) {
      setRightSideFolders(customFolders);
    }
  }, [customFolders]);

  const handleDrop = (folderData: DbFolder) => {
    const newFolder = {
      ...folderData,
      id: nextId,
      parent_id: null,
      isNew: true,
    };
    const updatedFolders = [...rightSideFolders, newFolder];
    setNextId(nextId - 1);
    setRightSideFolders(updatedFolders);
    setCustomFolders(updatedFolders);
  };

  const handleNest = (draggedId: number, targetId: number) => {
    const updatedFolders = rightSideFolders.map((folder) => {
      if (folder.id === draggedId) {
        return { ...folder, parent_id: targetId };
      }
      return folder;
    });
    setRightSideFolders(updatedFolders);
    setCustomFolders(updatedFolders);
  };

  const handleRemoveFolder = (folderId: number) => {
    const updatedFolders = rightSideFolders.filter(
      (folder) => folder.id !== folderId
    );
    setRightSideFolders(updatedFolders);
    setCustomFolders(updatedFolders);
  };

  const handleAddNewFolder = () => {
    const newFolder = {
      id: nextId,
      name: `New Folder ${Math.abs(nextId)}`,
      description: '',
      parent_id: null,
      isNew: true,
      is_locked: false,
      is_favorite: false,
    };
    const updatedFolders = [...rightSideFolders, newFolder];
    setNextId(nextId - 1);
    setRightSideFolders(updatedFolders);
    setCustomFolders(updatedFolders);
  };

  const isLoading =
    projectFoldersQuery.isLoading ||
    (templateId && templateFoldersQuery.isLoading);

  if (isLoading) {
    return (
      <div css={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  const existingFolders = projectFoldersQuery.data || [];
  const templateFolders = templateId ? templateFoldersQuery.data || [] : [];

  return (
    <div css={styles.container}>
      <div css={styles.twoColumn}>
        <div css={styles.leftColumn}>
          <div css={styles.columnHeader}>
            <FontAwesomeIcon
              icon={['fad', 'folder-tree']}
              css={styles.headerIconTemplate}
            />
            <Title level={4} css={styles.columnTitle}>
              {templateId
                ? intl.formatMessage({ id: 'template-folders' })
                : intl.formatMessage({ id: 'no-template-selected' })}
            </Title>
          </div>
          <div css={styles.columnContent}>
            {templateId ? (
              templateFoldersQuery.isLoading ? (
                <div css={styles.loadingState}>
                  <Spin />
                </div>
              ) : templateFoldersQuery.isError ? (
                <div css={styles.emptyState}>
                  <FontAwesomeIcon
                    icon={['fad', 'exclamation-triangle']}
                    css={styles.emptyIcon}
                  />
                  <div css={styles.emptyText}>
                    Failed to load template folders
                  </div>
                </div>
              ) : templateFolders.length > 0 ? (
                <DraggableFolderList folders={templateFolders} />
              ) : (
                <div css={styles.emptyState}>
                  <FontAwesomeIcon
                    icon={['fad', 'folder-open']}
                    css={styles.emptyIcon}
                  />
                  <div css={styles.emptyText}>
                    {intl.formatMessage({ id: 'no-template-folders' })}
                  </div>
                </div>
              )
            ) : (
              <div css={styles.emptyState}>
                <FontAwesomeIcon
                  icon={['fad', 'folder-open']}
                  css={styles.emptyIcon}
                />
                <div css={styles.emptyText}>
                  {intl.formatMessage({ id: 'starting-from-scratch' })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div css={styles.rightColumn}>
          <div css={styles.columnHeader}>
            <FontAwesomeIcon
              icon={['fad', 'folders']}
              css={styles.headerIcon}
            />
            <Title level={4} css={styles.columnTitle}>
              {intl.formatMessage({ id: 'project-structure' })}
            </Title>
            <Button
              type="primary"
              size="small"
              onClick={handleAddNewFolder}
              css={styles.addButton}>
              <FontAwesomeIcon icon={['fas', 'plus']} />
              {intl.formatMessage({ id: 'new-folder' })}
            </Button>
          </div>
          <div css={styles.columnContent}>
            <DroppableFolderList
              existingFolders={existingFolders}
              newFolders={rightSideFolders.filter((f) => f.isNew)}
              onDrop={handleDrop}
              onNest={handleNest}
              onRemove={handleRemoveFolder}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    padding: '0 16px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  loadingState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: 40,
  },
  twoColumn: {
    display: 'flex',
    gap: 24,
    minHeight: 350,
    maxHeight: 400,
  },
  leftColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    border: '2px solid #ff9800',
    borderRadius: 0,
    overflow: 'hidden',
  },
  rightColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    border: `2px solid ${theme.colors.primary}`,
    borderRadius: 0,
    overflow: 'hidden',
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px 20px',
    backgroundColor: theme.colors.backgroundSecondary,
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  headerIcon: {
    fontSize: 20,
    color: theme.colors.primary,
  },
  headerIconTemplate: {
    fontSize: 20,
    color: '#ff9800',
  },
  columnTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    flex: 1,
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 32,
    fontSize: 14,
    borderRadius: 6,
  },
  columnContent: {
    flex: 1,
    padding: 16,
    overflow: 'auto',
    backgroundColor: theme.colors.background,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: 40,
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: 64,
    color: theme.colors.textSecondary,
    opacity: 0.5,
    marginBottom: 16,
  },
  emptyText: {
    fontSize: 16,
    color: theme.colors.textSecondary,
  },
});
